import React from 'react';
import { Icons, getAppIcon } from '../components/ui/Icons.tsx';
import { ConnectedApp } from '../types';

interface IntegrationsPageProps {
  onClose: () => void;
  connectedApps: ConnectedApp[];
  onToggleApp: (id: string) => void;
}

const IntegrationsPage: React.FC<IntegrationsPageProps> = ({ onClose, connectedApps, onToggleApp }) => {

  return (
    <div className="w-full h-full flex flex-col bg-white dark:bg-[#050505] animate-in fade-in duration-300">
      
      {/* Header */}
      <div className="flex items-center justify-between px-10 py-8 border-b border-gray-100 dark:border-gray-800">
        <h2 className="text-2xl font-normal text-gray-900 dark:text-white tracking-tight">Add Integrations</h2>
        <button 
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all"
        >
          <Icons.X className="w-6 h-6" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-10 no-scrollbar">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {connectedApps.map((app) => {
            const Icon = getAppIcon(app.id);
            const isConnected = app.status === 'connected';
            const isExpired = app.status === 'expired';
            
            return (
              <div 
                key={app.id}
                className={`
                  group flex items-center justify-between p-6 rounded-2xl border transition-all duration-300 bg-white dark:bg-[#050505]
                  ${isConnected ? 'border-gray-200 dark:border-white/10 shadow-sm' : 
                    isExpired ? 'border-gray-200 dark:border-white/10 shadow-sm bg-gray-50/30 dark:bg-[#050505]' :
                    'border-gray-100 dark:border-white/5 hover:border-gray-200 dark:hover:border-white/15 hover:shadow-lg'}
                `}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 flex items-center justify-center shrink-0 transition-opacity ${isConnected || isExpired ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`}>
                    <Icon className="w-10 h-10 object-contain" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-normal text-gray-900 dark:text-white text-lg">{app.name}</span>
                    {isExpired && (
                      <span className="text-gray-500 dark:text-gray-400 text-[10px] font-bold uppercase tracking-widest leading-none mt-1">Connection Expired</span>
                    )}
                  </div>
                </div>
                
                <button 
                  onClick={() => onToggleApp(app.id)}
                  className={`
                    px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 border
                    ${isExpired 
                      ? 'bg-black dark:bg-white text-white dark:text-black border-black dark:border-white hover:bg-gray-800 dark:hover:bg-gray-100 hover:shadow-lg' 
                      : isConnected 
                        ? 'bg-gray-100 dark:bg-[#171717] text-black dark:text-white border-gray-200 dark:border-white/10 hover:bg-gray-200 dark:hover:bg-[#212121]' 
                        : 'bg-white dark:bg-[#050505] text-gray-900 dark:text-white border-gray-200 dark:border-white/10 hover:bg-black dark:hover:bg-white hover:text-white dark:hover:text-black hover:border-black dark:hover:border-white'}
                  `}
                >
                  {isExpired ? 'Reconnect' : (isConnected ? 'Disconnect' : 'Connect')}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default IntegrationsPage;
